import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { transfersApi } from '@/services/transfers.api';
import { tenancyApi } from '@/services/tenancy.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';

/**
 * Campus transfer history for one person — a request, and whatever the
 * destination campus decided. Approving or rejecting here is exactly the
 * same action a dedicated "pending transfers" queue offers; this is just
 * the same data seen from the person's own side.
 */
export function TransfersTab({
  personId,
  currentCampusId,
}: {
  readonly personId: string;
  readonly currentCampusId: string | null;
}) {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [isRequesting, setIsRequesting] = useState(false);

  const query = useQuery({
    queryKey: ['transfers', personId],
    queryFn: ({ signal }) => transfersApi.listForPerson(personId, signal),
  });
  const campuses = useQuery({
    queryKey: ['tenancy', 'campuses'],
    queryFn: ({ signal }) => tenancyApi.listCampuses(signal),
  });

  const campusName = (id: string | null): string =>
    id
      ? (campuses.data?.find((campus) => campus.id === id)?.name ?? id)
      : t('people.transfer.noCampus');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['transfers', personId], exact: false });

  const decide = useMutation({
    mutationFn: (input: { transferId: string; action: 'approve' | 'reject' | 'cancel' }) => {
      if (input.action === 'approve') return transfersApi.approve(input.transferId, { note: null });
      if (input.action === 'reject') return transfersApi.reject(input.transferId, { note: null });
      return transfersApi.cancel(input.transferId);
    },
    onSuccess: () => void invalidate(),
  });

  const transfers = query.data ?? [];

  return (
    <div className="transfers-tab">
      {can('people.transfer.create') &&
        (isRequesting ? (
          <RequestTransferForm
            personId={personId}
            campuses={(campuses.data ?? []).filter((campus) => campus.id !== currentCampusId)}
            onClose={() => setIsRequesting(false)}
            onCreated={() => {
              setIsRequesting(false);
              void invalidate();
            }}
          />
        ) : (
          <Button variant="primary" onClick={() => setIsRequesting(true)}>
            {t('people.transfer.request')}
          </Button>
        ))}

      {decide.isError && (
        <Alert tone="danger">
          {decide.error instanceof ApiError
            ? t(`errors.${decide.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {(query.isPending || campuses.isPending) && <Spinner label={t('common.states.loading')} />}
      {query.isSuccess && transfers.length === 0 && (
        <EmptyState title={t('people.transfer.empty')} />
      )}

      {transfers.length > 0 && (
        <ul className="transfer-list">
          {transfers.map((transfer) => (
            <li key={transfer.id} className="transfer-item">
              <div className="transfer-item-header">
                <span>
                  {campusName(transfer.fromCampusId)} → {campusName(transfer.toCampusId)}
                </span>
                <StatusBadge
                  status={transfer.status}
                  label={t(`people.transfer.status.${transfer.status}`)}
                />
              </div>
              <p className="field-hint">{transfer.reason}</p>
              <p className="field-hint" dir="ltr">
                {formatDate(transfer.requestedAt, locale)}
              </p>
              {transfer.status === 'PENDING' && (
                <div className="transfer-item-actions">
                  {can('people.transfer.manage') && (
                    <>
                      <Button
                        variant="primary"
                        loading={decide.isPending}
                        onClick={() =>
                          decide.mutate({ transferId: transfer.id, action: 'approve' })
                        }
                      >
                        {t('common.actions.approve')}
                      </Button>
                      <Button
                        variant="danger"
                        loading={decide.isPending}
                        onClick={() => decide.mutate({ transferId: transfer.id, action: 'reject' })}
                      >
                        {t('common.actions.reject')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    loading={decide.isPending}
                    onClick={() => decide.mutate({ transferId: transfer.id, action: 'cancel' })}
                  >
                    {t('common.actions.cancel')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestTransferForm({
  personId,
  campuses,
  onClose,
  onCreated,
}: {
  personId: string;
  campuses: readonly { id: string; name: string }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const [toCampusId, setToCampusId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => transfersApi.create(personId, { toCampusId, reason: reason.trim() }),
    onSuccess: onCreated,
    onError: (err: unknown) =>
      setError(
        err instanceof ApiError
          ? t(`errors.${err.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      ),
  });

  return (
    <form
      className="filters"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        if (toCampusId && reason.trim()) create.mutate();
      }}
    >
      {error && <Alert tone="danger">{error}</Alert>}

      <div className="field">
        <label className="field-label" htmlFor="transfer-campus">
          {t('people.transfer.toCampus')}
        </label>
        <select
          id="transfer-campus"
          className="field-input"
          value={toCampusId}
          onChange={(event) => setToCampusId(event.target.value)}
          required
        >
          <option value="">—</option>
          {campuses.map((campus) => (
            <option key={campus.id} value={campus.id}>
              {campus.name}
            </option>
          ))}
        </select>
      </div>

      <Field
        label={t('people.transfer.reason')}
        name="reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
      />

      <Button type="button" variant="ghost" onClick={onClose}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="primary" loading={create.isPending}>
        {t('common.actions.save')}
      </Button>
    </form>
  );
}
